import { useCallback, useEffect, useState, ComponentProps, FC } from 'react'
import ReactModal from 'react-modal'

import useIsServer from '../../hooks/useIsServer'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'

/**
 * Abstraction for working with modals whose content needs to scroll while disabling scrolling everywhere else.
 *
 * Returns:
 *  1. a ref function to attach to the element that needs to scroll, and
 *  2. the dom node that ref currently points to, which can be passed via props to the modal component
 */
export const useScrollableModalContent = (isOpen: boolean) => {
  const { enableBodyScroll, disableBodyScroll } = useBodyScrollLock()
  const [scrollableNode, setScrollableNode] = useState<HTMLElement>()

  const scrollableRefFn = useCallback((node: HTMLElement | null) => {
    if (node != null) {
      setScrollableNode(node)
    }
  }, [])

  useEffect(() => {
    if (scrollableNode) {
      if (isOpen) {
        disableBodyScroll(scrollableNode)
      } else {
        enableBodyScroll(scrollableNode)
      }
    }
  }, [isOpen, scrollableNode])

  return { scrollableRefFn, scrollableNode }
}

export type ModalProps = {
  onAfterOpen?: (...args: any) => any
  noScrollLock?: boolean
  scrollableNodes?: Element[]
} & ComponentProps<typeof ReactModal>

const Modal: FC<ModalProps> = (props) => {
  const {
    children,
    onAfterOpen,
    onRequestClose,
    noScrollLock,
    isOpen,
    scrollableNodes = [],
  } = props
  const isServer = useIsServer()

  /*
  We use a 3rd party library to prevent the document body from scrolling while the modal is open because iOS doesn't
  respect `overflow-y: hidden` on the body element. Instead, the library takes html nodes, and attaches event listeners
  to them so it they are still allowed to scroll, while disabling scrolling on everything else, also via event listeners.

  So our <Modal /> component can optionally be told which elements to still allow scrolling on -- it will pass these to
  the 3rd party library. It also attaches listeners to the .ReactModal__Content element -- this is done in the
  onAfterOpen handler to ensure the content element is there.
  */

  const { enableBodyScroll, disableBodyScroll, clearAllBodyScrollLocks } =
    useBodyScrollLock()

  const [defaultScrollableNode, setDefaultScrollableNode] = useState<Element>()

  useEffect(() => {
    if (defaultScrollableNode) {
      if (isOpen) {
        disableBodyScroll(defaultScrollableNode)
      } else {
        enableBodyScroll(defaultScrollableNode)
      }
    }
  }, [isOpen, defaultScrollableNode])

  useEffect(() => {
    if (noScrollLock !== true) {
      scrollableNodes?.forEach((node) => {
        if (node) {
          if (isOpen) {
            disableBodyScroll(node)
          } else {
            enableBodyScroll(node)
          }
        }
      })
    }
  }, [noScrollLock, isOpen, scrollableNodes.toString()])

  useEffect(() => {
    return () => {
      clearAllBodyScrollLocks()
    }
  }, [])

  const handleOpen = () => {
    if (!isServer && scrollableNodes.length === 0) {
      const modalContentElement = document.querySelector('.ReactModal__Content')
      setDefaultScrollableNode(modalContentElement)
    }

    if (onAfterOpen) {
      onAfterOpen()
    }
  }

  return (
    <ReactModal
      {...props}
      onAfterOpen={handleOpen}
      onAfterClose={clearAllBodyScrollLocks}
      onRequestClose={onRequestClose}
    >
      <>{children}</>
    </ReactModal>
  )
}

export default Modal
